import {
  ODSDropdownSelect,
  type ODSDropdownSelectProps,
  type ODSMenuListItemProps,
} from '@telekom-ods/react-ui-kit';
import { type CSSProperties, type FC } from 'react';
import { useIntl } from 'react-intl';

interface ODSSelectProps {
  id?: string;
  name: string;
  value?: string | number;
  onChange: ODSDropdownSelectProps['onValueChange'];
  onBlur?: () => void;
  label: string;
  menuItems?: ODSMenuListItemProps[];
  disabled?: boolean;
  helperText?: string;
  /**
   * Defaults to 'error' whenever helperText is set, since helperText is
   * normally Formik's validation error - override to 'informative' for a
   * plain hint. Matters on a disabled field: ODSDropdownSelect silently
   * drops the support message entirely when mode is 'error' and the field
   * is disabled (an "error style makes no sense on a disabled field" rule
   * baked into the design-system component), so an uncorrected 'error'
   * mode here doesn't just look wrong - it hides the text outright.
   */
  mode?: 'error' | 'informative';
  size?: 'small' | 'large';
  style?: CSSProperties;
}

export const ODSSelect: FC<ODSSelectProps> = ({
  id = '',
  name,
  value = '',
  onChange,
  onBlur,
  label,
  menuItems = [],
  disabled = false,
  helperText = '',
  mode: modeOverride,
  size = 'small',
  style,
}) => {
  const intl = useIntl();
  const mode = modeOverride ?? (helperText ? 'error' : 'informative');

  const menuItemsWithEmpty: ODSMenuListItemProps[] = [
    { value: '', label: intl.formatMessage({ id: 'noSelection' }) },
    ...menuItems,
  ];

  const selectedItem =
    menuItemsWithEmpty.find(item => item.value === value) ?? menuItemsWithEmpty[0];

  return (
    <ODSDropdownSelect
      id={id || name}
      size={size}
      value={selectedItem?.label ?? ''}
      {...(onChange && { onValueChange: onChange })}
      onBlur={onBlur}
      label={label}
      items={menuItemsWithEmpty}
      disabled={disabled}
      mode={mode}
      supportMessage={helperText}
      style={{ marginBottom: '1rem', minWidth: '300px', maxWidth: '400px', ...style }}
    />
  );
};
